import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'

import { desktopVw, mobileVw } from '../../styles/utils'

// Components
import Button from '../Button'

// Stage components
import { Alert, Input } from '../FormElements'

import Lock from '../../assets/encrypted_lock.svg'
import { editShippingAddress } from '../../utils/api'
import { getProvinceCodeDisplay } from '../../utils/address-display.utils'
import { captureWarning } from '../Sentry/utils'
import SpinningLoader from '../SpinningLoader'
import { accountStyleCTAButton } from './AccountStyle'
import AddressEditForm from './AddressForm'
import {
  getAllSavedAddress,
  getAllStatesOption,
} from '@src/quince-site-common/src/services/addressService/addressService'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import { useTranslation } from 'next-i18next'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { parseLocale } from '@src/utils/i18n.utils'
import { DEFAULT_COUNTRY } from '@src/config/localisation/locale-config.constants'
import styles from './EditShippingAdress.module.scss'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'

const Wrapper = styled.div``
const Form = styled.div`
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding-right: ${desktopVw(30)};
  }
`

const Footer = styled.div`
  /* margin-top: 30px; */

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: ${desktopVw(4)};
  }
`

const AlertContainer = styled.div`
  margin-bottom: ${mobileVw(16)};

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-bottom: ${desktopVw(16)};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: fit-content;
  height: 34px;
  margin-top: ${props => (props.noMargin ? 0 : mobileVw(14))};
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: ${desktopVw(17)};
    width: fit-content;
    max-width: 100%;
    height: ${desktopVw(34)};
  }
`

const AddressButton = styled.div`
  font-family: ${props => props.theme.fonts.grosa};
  color: ${props => props.theme.colors.primary};
  font-size: 11px;
  line-height: 17px;
  margin-top: 7px;
  text-decoration: underline;
  text-underline-offset: 5px;
  cursor: pointer;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(19)};
    margin-top: ${desktopVw(0)};
  }
`

const StyledButton = styled(Button)`
  ${accountStyleCTAButton}
  padding: 0;
  width: 100%;
  height: 100%;

  span {
    font-size: 1rem;
    line-height: 16px;
    padding: 0 3rem;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: 0;
    font-size: ${desktopVw(9)};
    line-height: ${desktopVw(15)};
  }
`

const SubmitButton = styled(Button)`
  ${accountStyleCTAButton}
  padding: 0;
  width: 167px;
  height: 53px;

  span {
    font-size: 10px;
    line-height: 16px;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: 0;
    font-size: ${desktopVw(9)};
    line-height: ${desktopVw(15)};
    width: ${desktopVw(164)};
    height: ${desktopVw(34)};
  }
`

const Heading = styled.p`
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 13px;
  line-height: 19px;
  font-weight: 500;
  margin-bottom: 8px;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(18)};
    line-height: ${desktopVw(24)};
    margin-bottom: ${desktopVw(8)};
  }
`
const SubHeading = styled.p`
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 11px;
  line-height: 17px;
  margin-bottom: 23px;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(16)};
    line-height: ${desktopVw(22)};
    margin-bottom: ${desktopVw(13)};
  }
`

const Img = styled.img`
  width: 65px;
  height: 86px
  background-color: grey;
  margin-right: 13px;
  object-fit: cover;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    width: ${desktopVw(70)};
    height: ${desktopVw(93)};
    margin-right: ${desktopVw(9)};
  }
`

const LineItemContainer = styled.div`
  margin-bottom: ${mobileVw(31)};
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-bottom: ${desktopVw(25)};
  }
`

const AddressDesc = styled.div``

const AddressBoxWrapper = styled.div`
  border-bottom: ${props => (props.showBorder ? '1px solid #e0e0e0' : 'none')};
  display: flex;
  padding: ${props => (props.showBorder ? `14px 14px 14px 0` : '0')};
  flex-direction: column;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    flex-direction: row;
    justify-content: space-between;
    padding: ${props => (props.showBorder ? `${desktopVw(14)} 0` : '0')};
  }
`

const AddressLine = styled.div`
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 13px;
  line-height: 19px;

  b {
    font-weight: 500;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(19)};
  }
`

const AddressWrapper = styled.section`
  border-top: 1px solid #e0e0e0;
  padding: ${mobileVw(20)} 0;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: ${desktopVw(20)} 0;
    overflow: hidden;
    overflow-y: scroll;
    height: 720px;
    padding-right: ${desktopVw(30)};
  }
`

const AddressSectionHeadingLine = styled.div`
  display: flex;
  justify-content: space-between;
`

const AddressSectionHeading = styled.p`
  font-weight: 500;
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 13px;
  line-height: 19px;
  margin-bottom: 9px;
  text-transform: uppercase;

  span {
    text-decoration: underline;
    text-underline-offset: 3px;
    text-transform: none;
    font-weight: 400;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(19)};
    margin-bottom: ${desktopVw(15)};
  }
`

const AddressSectionSubHeading = styled.p`
  font-family: ${props => props.theme.fonts.grosa};
  font-size: 13px;
  line-height: 19px;
  margin-bottom: 23px;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    font-size: ${desktopVw(13)};
    line-height: ${desktopVw(19)};
    margin-bottom: ${desktopVw(13)};
  }
`

const AddNewAddressWrapper = styled.section`
  border-top: 0.54px solid rgba(33, 32, 31, 0.4);
  padding: 30px 0px 33px 0px;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding: ${desktopVw(35)} 0 ${desktopVw(35)} 0;
  }
`

const PaymentWrapper = styled.div`
  padding-top: ${mobileVw(13)};
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding-top: ${desktopVw(16)};
  }
`

const StyledPaymentInput = styled(Input)`
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-right: ${desktopVw(16)};
  }
`

const StyledLock = styled(Lock)`
  position: absolute;
  top: ${mobileVw(33)};
  right: ${mobileVw(16)};

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    top: ${desktopVw(33)};
    right: ${desktopVw(16)};
  }
`

const StyledPaymentInputWrapper = styled.div`
  position: relative;
  padding-top: ${mobileVw(16)};
  display: flex;
  flex-direction: column;
  align-items: center;

  .card-number-input {
    width: 100%;
    margin-bottom: ${mobileVw(21)};
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    padding-top: ${desktopVw(16)};

    .card-number-input {
      width: 100%;
      margin-bottom: ${desktopVw(21)};
    }
  }
`
const StyledLoader = styled.div`
  margin-top: ${mobileVw(100)};
  position: relative;
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin-top: ${desktopVw(100)};
  }
`

export const DisplayAddress = ({
  address: { firstName, lastName, company, address1, address2, city, provinceCode, zip, country, id, province },
  title,
  showBtn,
  onButtonClick,
  showBorder,
  loading,
}) => {
  const { t } = useTranslation('common')
  const { skipProvinceInAddress } = useStatsigDynamicLocaleConfig()
  const [clicked, setClicked] = useState(false)
  useEffect(() => {
    if (!loading && clicked) {
      setClicked(false)
    }
  }, [loading, clicked])
  // Locales that don't use a province/state (e.g. UK) omit the province code from the address line.
  const provinceDisplay = getProvinceCodeDisplay({ provinceCode, province, skipProvinceInAddress })
  return (
    <AddressBoxWrapper showBorder={showBorder}>
      <AddressDesc>
        {(title || company) && (
          <AddressLine>
            <b>{title || company}</b>
          </AddressLine>
        )}
        <AddressLine>
          {firstName} {lastName}
        </AddressLine>
        {company && title && <AddressLine>{company}</AddressLine>}
        <AddressLine>{address1}</AddressLine>
        {address2 && <AddressLine>{address2}</AddressLine>}
        {/* @i18n-ignore */}
        <AddressLine>{[city, provinceDisplay, zip].filter(Boolean).join(' ')}</AddressLine>
        <AddressLine>{country}</AddressLine>
      </AddressDesc>
      {showBtn && (
        <AddressButton
          black
          // @i18n-ignore
          type="button"
          loading={clicked}
          onClick={() => {
            setClicked(true)
            if (id) onButtonClick(id)
          }}
        >
          {t('ship_to_this_address')}
        </AddressButton>
      )}
    </AddressBoxWrapper>
  )
}

const AddAddressForm = ({
  setShowAddressFields,
  formData,
  handleSetFormData,
  addAddressForm,
  showAddressFields,
  editOrderLoading,
  validateAddress,
  stateOptions,
  countryCode,
}) => {
  const { t } = useTranslation('common')
  return (
    <>
      <AddNewAddressWrapper>
        <AddressSectionHeading>{t('add_a_new_address')}</AddressSectionHeading>
        <AddressSectionSubHeading>{t('create_a_new_shipping_address')}</AddressSectionSubHeading>
        {!showAddressFields ? (
          <ButtonContainer noMargin>
            <StyledButton
              black
              // @i18n-ignore
              type="button"
              onClick={() => {
                setShowAddressFields(true)
              }}
            >
              {t('add_a_new_address_v1')}
            </StyledButton>
          </ButtonContainer>
        ) : (
          <AddressEditForm
            formData={formData}
            setFormData={handleSetFormData}
            addAddressForm={addAddressForm}
            editOrderLoading={editOrderLoading}
            validateAddress={validateAddress}
            stateOptions={stateOptions}
            countryCode={countryCode}
          />
        )}
      </AddNewAddressWrapper>
    </>
  )
}

const EditAddressForm = ({
  editAddressFields,
  formData,
  cardNumber,
  setCardNumber,
  editOrderLoading,
  handleSubmit,
  setShowAddressFields,
}) => {
  const { t } = useTranslation('common')
  return (
    <>
      <AddNewAddressWrapper>
        <AddressSectionHeadingLine>
          <AddressSectionHeading>{t('new_address')}</AddressSectionHeading>
          <AddressSectionHeading onClick={editAddressFields}>
            <span>{t('edit')}</span>
          </AddressSectionHeading>
        </AddressSectionHeadingLine>
        <DisplayAddress address={formData} />
        <PaymentWrapper>
          <AddressSectionHeading>{t('confirm_your_payment_method')}</AddressSectionHeading>
          <StyledPaymentInputWrapper>
            <StyledPaymentInput
              placeholder={t('card_number')}
              name="cardNumber"
              // @i18n-ignore
              type="number"
              value={cardNumber}
              onChange={e => {
                const card = e.target.value
                if (card.length > 16) return
                setCardNumber(card)
              }}
              // @i18n-ignore
              containerClassName="card-number-input"
            />
            <StyledLock />
            <SubmitButton
              black
              // @i18n-ignore
              type="button"
              loading={editOrderLoading}
              onClick={() => {
                handleSubmit()
                setShowAddressFields(true)
              }}
            >
              {t('submit_v2')}
            </SubmitButton>
          </StyledPaymentInputWrapper>
        </PaymentWrapper>
      </AddNewAddressWrapper>
    </>
  )
}

const getProvinceCode = address => {
  const code = address?.provinceCode ?? address?.province_code
  return code?.trim().toUpperCase()
}

const getProvince = address => {
  const provinceName = address?.province ?? address?.provinceName ?? address?.province_name
  return provinceName?.trim().toLowerCase()
}

const isStateChange = (newAddress, currentAddress, skipProvinceInAddress) => {
  if (skipProvinceInAddress) return false
  const currentProvinceCode = getProvinceCode(currentAddress)
  const newProvinceCode = getProvinceCode(newAddress)
  if (currentProvinceCode && newProvinceCode) {
    return currentProvinceCode !== newProvinceCode
  }
  return getProvince(currentAddress) !== getProvince(newAddress)
}

const EditAddress = ({ onSuccess, activeOrder, addAddressForm, onBeforeSubmit }) => {
  const { t } = useTranslation('common')
  const formDataRef = useRef({})
  const [validationError, setValidationError] = useState('')
  const [showPaymentStep, setShowPaymentStep] = useState(false)
  const [cardNumber, setCardNumber] = useState('')
  const [editOrderLoading, setEditOrderLoading] = useState(false)
  const [showAddressFields, setShowAddressFields] = useState(false)
  const [stateOptions, setStateOptions] = useState([])
  const [addressEditStateError, setAddressEditStateError] = useState(false)
  const selectSavedAddressHeadingRef = useRef(null)

  const { skipProvinceInAddress } = useStatsigDynamicLocaleConfig()

  const { locale } = useLocaleContext()
  const { country } = parseLocale(locale)
  const {
    fetchData,
    response: savedAddressData,
    isLoading: savedAddressDataLoading,
  } = useFetch(getAllSavedAddress, {
    initialData: [],
    initialLoadingState: true,
    onlyReturnLatestFetch: true,
  })

  useEffect(() => {
    getAllStatesOption(country.toUpperCase()).then(stateOptions => {
      setStateOptions(stateOptions.states)
    })
  }, [country])

  useEffect(() => {
    fetchData()
  }, [])

  const valid = id => {
    if (!id && !cardNumber) {
      setValidationError('Please add Card Number before proceeding')

      return false
    }
    setValidationError('')
    return true
  }

  const executeAddressUpdate = async (id, address, updateCustomerSubscription = false) => {
    try {
      setEditOrderLoading(true)
      await editShippingAddress({
        activeOrder,
        id,
        address,
        cardNumber,
        setValidationError,
        onSuccess,
        stateOptions,
        t,
        skipProvinceInAddress,
        updateCustomerSubscription,
      })
      setEditOrderLoading(false)
    } catch (error) {
      setEditOrderLoading(false)
      captureWarning(
        `Edit Shipping Address API request failed with following error: ${error}`,
        { activeOrder },
        {
          // @i18n-ignore
          API: 'Edit Shipping Address',
          type: 'API_FAILURE',
        },
      )
    }
  }

  const handleSubmit = async (id, addressObj) => {
    const address = Object.keys(formDataRef.current).length > 0 ? formDataRef.current : addressObj
    address.country_code = country.toUpperCase() || DEFAULT_COUNTRY.toUpperCase()

    if (onBeforeSubmit) {
      onBeforeSubmit(updateCustomerSubscription => executeAddressUpdate(id, address, updateCustomerSubscription))
    } else {
      await executeAddressUpdate(id, address)
    }
  }

  const handleSetFormData = val => {
    formDataRef.current = val
    handleSubmit()
  }

  const editAddressFields = () => {
    setValidationError(false)
    setShowPaymentStep(false)
  }

  const setSelectedAddress = (val, address) => {
    const addressData = {
      address1: address?.addressLine1,
      address2: address?.addressLine2 || '',
      city: address?.city,
      company: address?.company || '',
      country: address?.countryName,
      first_name: address?.firstName,
      last_name: address?.lastName,
      phone: address?.phoneNumber || '',
      province: address?.provinceName,
      zip: address?.zip,
    }
    handleSubmit(val, addressData)
  }

  return (
    <>
      <Wrapper>
        <Form>
          <Heading>
            {t('order_v2')}
            {activeOrder?.orderNumber}
          </Heading>
          <SubHeading>{t('items_in_this_shipment')}</SubHeading>
          <LineItemContainer>
            {(activeOrder?.allLineItems ?? []).map(lineItem => (
              <Img key={lineItem.id} loading="lazy" src={lineItem.image} />
            ))}
          </LineItemContainer>
          <AddNewAddressWrapper>
            <AddressSectionHeading>{t('current_shipping_address')}</AddressSectionHeading>
            <DisplayAddress address={activeOrder?.shippingAddress} />
          </AddNewAddressWrapper>
          {!showPaymentStep ? (
            <AddAddressForm
              setShowAddressFields={setShowAddressFields}
              formData={formDataRef.current}
              handleSetFormData={handleSetFormData}
              addAddressForm={addAddressForm}
              showAddressFields={showAddressFields}
              editOrderLoading={editOrderLoading}
              validateAddress={address => {
                return !isStateChange(address, activeOrder?.shippingAddress, skipProvinceInAddress)
              }}
              stateOptions={stateOptions}
              countryCode={country}
            />
          ) : (
            <EditAddressForm
              editAddressFields={editAddressFields}
              formData={formDataRef.current}
              cardNumber={cardNumber}
              setCardNumber={setCardNumber}
              editOrderLoading={editOrderLoading}
              handleSubmit={handleSubmit}
              setShowAddressFields={setShowAddressFields}
            />
          )}

          {validationError && (
            <Footer>
              <AlertContainer>
                <Alert message={validationError} />
              </AlertContainer>
            </Footer>
          )}
        </Form>
        {savedAddressDataLoading ? (
          <StyledLoader>
            <SpinningLoader />
          </StyledLoader>
        ) : (
          (savedAddressData || []).length > 0 && (
            <AddressWrapper>
              <AddressSectionHeading ref={selectSavedAddressHeadingRef}>
                {t('select_another_address')}
              </AddressSectionHeading>
              {addressEditStateError ? (
                <div className={styles.errorMessageWrapper}>{t('address_edit_state_error')}</div>
              ) : null}
              {(savedAddressData || []).map((address, index) => {
                return (
                  <DisplayAddress
                    showBorder
                    showBtn
                    address={{
                      id: address?.id,
                      firstName: address?.firstName,
                      lastName: address?.lastName,
                      company: address?.company,
                      address1: address?.addressLine1,
                      address2: address?.addressLine2,
                      country: address?.countryName,
                      city: address?.city,
                      provinceCode: address?.provinceCode,
                      province: address?.provinceName,
                      zip: address?.zip,
                    }}
                    title={t('address_var1', { var1: index + 1 })}
                    key={address?.id}
                    loading={editOrderLoading}
                    onButtonClick={val => {
                      setAddressEditStateError(false)
                      if (isStateChange(address, activeOrder?.shippingAddress, skipProvinceInAddress)) {
                        setAddressEditStateError(true)
                        setEditOrderLoading(false)
                        selectSavedAddressHeadingRef.current?.scrollIntoView({ behavior: 'smooth' })
                        return
                      }
                      setSelectedAddress(val, address)
                    }}
                  />
                )
              })}
            </AddressWrapper>
          )
        )}
      </Wrapper>
    </>
  )
}

export default EditAddress
